import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText'];

  /**
   * Visible numeric count node (not the a11y label).
   * @returns {HTMLElement | null}
   */
  get #countElement() {
    return this.refs.cartBubbleText.querySelector('.cart-bubble__text-count');
  }

  /** @type {number} */
  get currentCartCount() {
    const raw = this.#countElement?.textContent?.trim() ?? '0';
    const count = parseInt(raw, 10);
    return Number.isFinite(count) ? count : 0;
  }

  set currentCartCount(value) {
    const count = Number.isFinite(value) ? value : 0;
    const countElement = this.#countElement;

    if (countElement) {
      if (count >= 100) {
        countElement.textContent = '';
      } else {
        countElement.textContent = String(Math.max(0, count));
      }
    } else if (count > 0 && count < 100) {
      this.refs.cartBubbleText.textContent = String(count);
    }

    const hidden = this.refs.cartBubbleText.querySelector('.visually-hidden');
    if (hidden) {
      const label = hidden.textContent?.split(':')[0]?.trim() || 'Cart count';
      hidden.textContent = `${label}: ${count}`;
    }

    this.refs.cartBubble.toggleAttribute('data-maintain-ratio', count > 0 && count <= 99);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    const nextCount = comingFromProductForm
      ? this.currentCartCount + itemCount
      : itemCount;

    this.refs.cartBubble.classList.toggle('visually-hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', nextCount > 0 && animate);
    this.currentCartCount = nextCount;

    this.classList.toggle('hp-native-cart-icon--has-cart', nextCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      const count = parseInt(value, 10);

      if (Date.now() - timestamp < 10000 && Number.isFinite(count) && count >= 0) {
        if (count !== this.currentCartCount) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
